import { useParams } from 'react-router-dom';
import Sticky from 'react-stickynode';

import { Box } from '@hivebrite/design-system/Box';
import { Container } from '@hivebrite/design-system/Container';
import { useBreakpoint } from '@hivebrite/design-system/Theme';

import TitleBlock from 'hb-react/frontoffice/components/BlocksRenderer/blocks/Items/TitleBlock';
import {
  BLOCKS,
  BlockWrapper,
} from 'hb-react/frontoffice/components/BlocksRenderer/shared';
import Grid, { Row, Col } from 'hb-react/shared/components/DesignSystem/Grid';
import useDisplayNavigationDropShadow from 'hb-react/shared/hooks/theme/useDisplayNavigationDropShadow';
import useSecondaryNavigationTheme from 'hb-react/shared/hooks/theme/useSecondaryNavigationTheme';
import ThemeProvider from 'hb-react/style/ThemeProvider';

import TemplateContainer from '../common/TemplateContainer';
import { DEFAULT_ZONE } from '../utils';

const ZONE_IDS = {
  appHeader: 'appHeader',
  headerControls: 'headerControls',
  filters: 'filters',
  before: 'before',
  tags: 'tags',
  right: 'right',
  empty: 'empty',
};

export const DEFAULT_ZONES = {
  appHeader: [BLOCKS.appHeader.id],
  headerControls: [BLOCKS.headerControls.id],
  filters: [BLOCKS.filters.id],
  before: [BLOCKS.slider.id],
  tags: [BLOCKS.tags.id],
  right: [BLOCKS.details.id],
  empty: [BLOCKS.emptyContent.id, BLOCKS.conditionalBlock.id],
};

const EmbeddedDetailsTemplate = ({
  colWidth,
  data,
  getSelectedId,
  hasFilters,
  topicId,
  variant,
  zones,
}) => {
  const params = useParams();
  const displayNavigationDropShadow = useDisplayNavigationDropShadow();
  const secondaryNavigationTheme = useSecondaryNavigationTheme();
  const breakpoint = useBreakpoint();

  const selectedId = getSelectedId(data, params);
  const hideDetails = ['s', 'm'].includes(breakpoint) && !selectedId;
  const detailsOnly = ['s', 'm'].includes(breakpoint) && selectedId;

  if (detailsOnly) {
    return (
      <Container>
        <Grid width="100%" mx={breakpoint === 's' ? 0 : undefined}>
          <Row>
            <Col width={{ _: 1, l: 10 / 12 }} offset={{ _: 0, l: 1 / 12 }}>
              <Box pb={9} display="block" mt={{ _: 0, m: 10 }}>
                {zones[ZONE_IDS.right]?.map((block, index) => (
                  <BlockWrapper
                    key={index}
                    mt={{ _: 0, m: -4 }}
                    $emptyStyle={{ marginTop: 0 }}
                  >
                    {block}
                  </BlockWrapper>
                ))}
              </Box>
            </Col>
          </Row>
        </Grid>
      </Container>
    );
  }

  return (
    <TemplateContainer id="embedded-details-template">
      {!topicId && zones[ZONE_IDS.appHeader] && (
        <BlockWrapper
          withShadow={
            displayNavigationDropShadow && variant !== 'max' && hasFilters
          }
        >
          {zones[ZONE_IDS.appHeader]}
        </BlockWrapper>
      )}
      {zones[ZONE_IDS.headerControls] && (
        <Container>
          <Grid width="100%">
            <Row>
              <Col width={1}>
                <BlockWrapper
                  mt={8}
                  mb={-3}
                  $emptyStyle={{ marginTop: 0, marginBottom: 0 }}
                >
                  {zones[ZONE_IDS.headerControls]}
                </BlockWrapper>
              </Col>
            </Row>
          </Grid>
        </Container>
      )}
      {zones[ZONE_IDS.filters] && (
        <ThemeProvider
          theme={secondaryNavigationTheme.themeName}
          fontBrightness={secondaryNavigationTheme.fontBrightness}
        >
          <BlockWrapper
            py={6}
            bg={secondaryNavigationTheme.backgroundColor}
            withShadow={displayNavigationDropShadow}
          >
            <Container height="auto" display="block">
              <Grid rowGap={0}>
                <Row>
                  <Col width={colWidth}>{zones[ZONE_IDS.filters]}</Col>
                </Row>
              </Grid>
            </Container>
          </BlockWrapper>
        </ThemeProvider>
      )}
      <Container height="auto" display="block" pb={6}>
        <Grid rowGap={0}>
          <Row>
            <Col width={colWidth}>
              {zones[ZONE_IDS.before]?.map((block, index) => (
                <BlockWrapper key={index} mt={6} $emptyStyle={{ marginTop: 0 }}>
                  {block}
                </BlockWrapper>
              ))}
            </Col>
          </Row>
          <Row>
            <Col width={colWidth}>
              {zones[ZONE_IDS.tags]?.map((block, index) => (
                <BlockWrapper
                  key={index}
                  mt={13}
                  $emptyStyle={{ marginTop: 0 }}
                >
                  {block}
                </BlockWrapper>
              ))}
            </Col>
          </Row>
          <Row>
            <Col width={{ _: 1, l: 4 / 12 }}>
              {zones[DEFAULT_ZONE] ? (
                <Box flexDirection="column">
                  {zones[DEFAULT_ZONE].map((block, index) => (
                    <BlockWrapper
                      key={index}
                      mt={6}
                      $emptyStyle={{ marginTop: 0 }}
                    >
                      {block}
                    </BlockWrapper>
                  ))}
                </Box>
              ) : null}
            </Col>
            <Col width={{ _: 1, l: 8 / 12 }}>
              {!hideDetails && zones[ZONE_IDS.right] ? (
                <Box flexDirection="column" mt={6}>
                  {/* This is to ensure we are aligned with the Items block spacing on the left */}
                  <TitleBlock title="&nbsp;" />
                  <Sticky
                    enabled={['l', 'xl'].includes(breakpoint)}
                    top="header"
                    bottomBoundary="#embedded-details-template"
                  >
                    {zones[ZONE_IDS.right].map((block, index) => (
                      <BlockWrapper
                        key={index}
                        pt={4}
                        pb={5}
                        $emptyStyle={{ paddingTop: 0, paddingBottom: 0 }}
                      >
                        {block}
                      </BlockWrapper>
                    ))}
                  </Sticky>
                </Box>
              ) : null}
            </Col>
          </Row>
          <Row>
            <Col>
              {zones[ZONE_IDS.empty] && (
                <BlockWrapper>{zones[ZONE_IDS.empty]}</BlockWrapper>
              )}
            </Col>
          </Row>
        </Grid>
      </Container>
    </TemplateContainer>
  );
};

export default {
  id: 'embedded_details',
  zones: DEFAULT_ZONES,
  component: EmbeddedDetailsTemplate,
};
